import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { 
  ChevronLeft, ChevronRight, X, 
  UserPlus, Gavel, ShoppingBag, Bell, MessageCircle, Heart,
  DollarSign, ArrowRightLeft, Settings, Gift
} from "lucide-react";

const TUTORIAL_STEPS = [
  {
    title: "¡Bienvenido a Clueer! 🇵🇦",
    description: "Tu plataforma de subastas y ventas directas de tecnología. Te guiaremos por las funciones principales.",
    icon: null,
    image: null,
    tips: [
      "Participa en Subastas en Vivo o envía tu Mejor Oferta",
      "Todas las transacciones son pago contraentrega",
      "Revisas el producto antes de pagar"
    ]
  },
  {
    title: "Registro Rápido",
    description: "Crear cuenta es súper fácil - ¡usa tu WhatsApp!",
    icon: UserPlus,
    tips: [
      "Regístrate con tu número de WhatsApp (8 dígitos)",
      "Tu número será tu contraseña para ingresar",
      "También puedes usar correo o Google",
      "¡Envía ofertas sin registrarte desde el producto!"
    ]
  },
  {
    title: "Subastas en Vivo",
    description: "Participa en subastas y gana productos al mejor precio.",
    icon: Gavel,
    tips: [
      "Haz clic en 'Participar en la Subasta' para ofertar",
      "Cada subasta tiene un temporizador visible",
      "Si alguien supera tu oferta, recibirás una notificación",
      "Opción 'Solicitar Compra sin Subasta' para comprar directo"
    ]
  },
  {
    title: "Mejor Oferta Aceptada",
    description: "Envía tu mejor oferta y negocia el precio.",
    icon: ShoppingBag,
    tips: [
      "Haz clic en 'Enviar una oferta' con tu precio propuesto",
      "Sugerimos ofrecer 5% menos para mayor aceptación",
      "El vendedor puede aceptar, rechazar o contraofertar",
      "Tienes máximo 3 contrapropuestas por producto"
    ]
  },
  {
    title: "Sistema de Ofertas",
    description: "Negocia el mejor precio con los vendedores.",
    icon: ArrowRightLeft,
    tips: [
      "Tus ofertas aparecen en 'Ofertas' en tu perfil",
      "El vendedor puede contraofertar hasta 3 veces",
      "Mantente pendiente de las notificaciones",
      "Si aceptas una contraoferta, el producto es tuyo"
    ]
  },
  {
    title: "🎁 Recompensas y Cashback",
    description: "¡Gana dinero dejando reviews de tus compras!",
    icon: Gift,
    tips: [
      "Después de pagar tu producto, recibirás una invitación de review",
      "Deja tu opinión honesta sobre el producto y la entrega",
      "Incluye tu número de Yappy para recibir el pago",
      "¡Gana mínimo $2 de cashback por cada review!"
    ]
  },
  {
    title: "Notificaciones",
    description: "Mantente informado de toda la actividad.",
    icon: Bell,
    tips: [
      "Alertas cuando superen tu oferta en subastas",
      "Notificaciones de ofertas y contraofertas",
      "Avisos cuando ganes una subasta o acepten tu oferta",
      "Invitaciones para dejar reviews y ganar cashback"
    ]
  },
  {
    title: "Mensajes",
    description: "Comunícate directamente con vendedores.",
    icon: MessageCircle,
    tips: [
      "Pregunta detalles sobre los productos",
      "Coordina la entrega contraentrega",
      "Los mensajes son en tiempo real"
    ]
  },
  {
    title: "Favoritos",
    description: "Guarda los productos que te interesan.",
    icon: Heart,
    tips: [
      "Haz clic en el corazón para agregar a favoritos",
      "Recibe notificaciones de actividad en tus favoritos",
      "Accede rápido desde tu perfil"
    ]
  },
  {
    title: "Cupones de Descuento",
    description: "Ahorra con códigos promocionales.",
    icon: DollarSign,
    tips: [
      "Algunos productos tienen cupones disponibles",
      "Ingresa el código antes de confirmar la compra",
      "El descuento se aplica automáticamente"
    ]
  },
  {
    title: "¡Listo para empezar!",
    description: "Ya conoces las funciones principales de Clueer. ¡Explora y encuentra las mejores ofertas!",
    icon: Settings,
    tips: [
      "Explora 'Subastas en Vivo' y 'Mejor Oferta Aceptada'",
      "Completa tu perfil con tu WhatsApp",
      "¡Buena suerte en tus compras!"
    ]
  }
];

const TutorialModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === TUTORIAL_STEPS.length - 1;
  const Icon = step.icon;

  const handleNext = () => {
    if (isLast) {
      onClose();
      // Mark tutorial as seen
      localStorage.setItem('ibiid_tutorial_seen', 'true');
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('ibiid_tutorial_seen', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <Card className="w-full max-w-lg relative overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-auto">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#F1F5F9] text-[#64748B] hover:text-[#0F172A] transition-colors z-10"
          data-testid="tutorial-close"
        >
          <X size={20} />
        </button>

        <CardContent className="p-0">
          {/* Progress bar */}
          <div className="h-1 bg-[#E2E8F0]">
            <div 
              className="h-full bg-[#475569] transition-all duration-300"
              style={{ width: `${((currentStep + 1) / TUTORIAL_STEPS.length) * 100}%` }}
            />
          </div>

          <div className="p-6">
            {/* Step indicator */}
            <div className="flex justify-center gap-1.5 mb-6">
              {TUTORIAL_STEPS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? "bg-[#475569] w-6" 
                      : idx < currentStep 
                        ? "bg-[#94A3B8]" 
                        : "bg-[#E2E8F0]"
                  }`}
                />
              ))}
            </div>

            {/* Icon */}
            {Icon && (
              <div className="w-16 h-16 rounded-2xl bg-[#F1F5F9] flex items-center justify-center mx-auto mb-4">
                <Icon size={32} className="text-[#475569]" />
              </div>
            )}

            {/* Title */}
            <h2 className="text-xl font-bold text-[#0F172A] text-center mb-2">
              {step.title}
            </h2>

            {/* Description */}
            <p className="text-[#64748B] text-center mb-6">
              {step.description}
            </p>

            {/* Tips */}
            <div className="bg-[#F8FAFC] rounded-xl p-4 mb-6">
              <ul className="space-y-2">
                {step.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#475569]">
                    <span className="w-5 h-5 rounded-full bg-[#475569] text-white flex items-center justify-center flex-shrink-0 text-xs font-medium">
                      {idx + 1}
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={isFirst}
                className="text-[#64748B]"
                data-testid="tutorial-prev"
              >
                <ChevronLeft size={18} className="mr-1" />
                Anterior
              </Button>

              <span className="text-sm text-[#94A3B8]">
                {currentStep + 1} / {TUTORIAL_STEPS.length}
              </span>

              <Button
                onClick={handleNext}
                className="bg-[#475569] hover:bg-[#334155] text-white"
                data-testid="tutorial-next"
              >
                {isLast ? "¡Empezar!" : "Siguiente"}
                {!isLast && <ChevronRight size={18} className="ml-1" />}
              </Button>
            </div>

            {/* Skip link */}
            {!isLast && (
              <button
                onClick={handleSkip}
                className="w-full text-center text-sm text-[#94A3B8] hover:text-[#64748B] mt-4"
                data-testid="tutorial-skip"
              >
                Saltar tutorial
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to manage tutorial state
export const useTutorial = () => {
  const [showTutorial, setShowTutorial] = useState(false);

  const openTutorial = () => setShowTutorial(true);
  const closeTutorial = () => setShowTutorial(false);

  // Check if should auto-show for new users
  const shouldAutoShow = () => {
    return !localStorage.getItem('ibiid_tutorial_seen');
  };

  return {
    showTutorial,
    openTutorial,
    closeTutorial,
    shouldAutoShow,
    TutorialModal: (props) => <TutorialModal isOpen={showTutorial} onClose={closeTutorial} {...props} />
  };
};

export default TutorialModal;
